import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WidgetProps } from '@/lib/widgets/types';
import { Route, Navigation, MapPin, Clock, Gauge, Eye } from 'lucide-react';

export function LiveRouteWidget({ isEditing = false }: WidgetProps) {
  const [selectedVehicle, setSelectedVehicle] = useState('ABC-1234');

  const vehicles = [
    { id: 'ABC-1234', driver: 'João Silva', status: 'moving' },
    { id: 'DEF-5678', driver: 'Maria Santos', status: 'stopped' },
    { id: 'GHI-9012', driver: 'Pedro Costa', status: 'moving' }
  ];

  const currentRoute = {
    origin: 'Depósito Central - SP',
    destination: 'Cliente ABC Ltda - RJ',
    progress: 65,
    estimatedArrival: '14:30',
    currentLocation: 'Rod. Presidente Dutra, km 165',
    speed: 68,
    distance: { total: 450, remaining: 158 },
    status: 'on_time'
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_time': return 'bg-green-100 text-green-700';
      case 'delayed': return 'bg-red-100 text-red-700';
      case 'early': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Route className="h-4 w-4" />
            Rota Ao Vivo
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={isEditing}>
            <Eye className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select
          value={selectedVehicle}
          onValueChange={setSelectedVehicle}
          disabled={isEditing}
        >
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {vehicles.map((vehicle) => (
              <SelectItem key={vehicle.id} value={vehicle.id}>
                {vehicle.id} - {vehicle.driver}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Route Info */}
        <div className="space-y-3">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3 text-green-600" />
                <span className="text-xs font-medium">Origem</span>
              </div>
              <p className="text-xs text-muted-foreground pl-5">{currentRoute.origin}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="w-2 h-8 bg-gradient-to-b from-green-500 via-yellow-500 to-red-500 rounded-full"></div>
            <div className="flex-1">
              <div className="bg-muted rounded-full h-2 mb-1">
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${currentRoute.progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-muted-foreground">{currentRoute.progress}% concluído</p>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3 text-red-600" />
                <span className="text-xs font-medium">Destino</span>
              </div>
              <p className="text-xs text-muted-foreground pl-5">{currentRoute.destination}</p>
            </div>
          </div>
        </div>

        {/* Current Status */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Localização atual:</span>
            <Badge className={getStatusColor(currentRoute.status)} variant="outline">
              No horário
            </Badge>
          </div>
          <p className="text-xs font-medium">{currentRoute.currentLocation}</p>
          
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Gauge className="h-3 w-3" />
                <span className="text-xs font-bold">{currentRoute.speed}</span>
              </div>
              <p className="text-xs text-muted-foreground">km/h</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Navigation className="h-3 w-3" />
                <span className="text-xs font-bold">{currentRoute.distance.remaining}</span>
              </div>
              <p className="text-xs text-muted-foreground">km rest.</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Clock className="h-3 w-3" />
                <span className="text-xs font-bold">{currentRoute.estimatedArrival}</span>
              </div>
              <p className="text-xs text-muted-foreground">chegada</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}